import React, { useState, useEffect, useRef } from "react";
import {
  getLocationsApi,
  searchLocationsApi,
  deleteLocationApi,
  getLocationDetailApi,
} from "@/lib/api/location";
import { useModal } from "@/store/modalStore";
import { useLoading } from "@/store/loadingStore";
import { regions, getRegionByCode } from "@/constants/regions";

// 나주시 지역 데이터만 필터링
const najuRegions = regions.filter((region) => region.fullName.includes("나주시"));

// 나주시 읍면동 목록
const najuDistricts = najuRegions
  .filter(
    (region) =>
      region.code.startsWith("4617") && region.code.endsWith("000") && region.code !== "4617000000"
  )
  .sort((a, b) => {
    const order = ["면", "읍", "동"];
    const aType = order.findIndex((type) => a.name.endsWith(type));
    const bType = order.findIndex((type) => b.name.endsWith(type));

    if (aType !== bType) return aType - bType;
    return a.name.localeCompare(b.name);
  });

const LocationListView = () => {
  const modal = useModal();
  const { startLoading, stopLoading } = useLoading();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedLocationDetail, setSelectedLocationDetail] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchType, setSearchType] = useState("동");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedSensorTab, setSelectedSensorTab] = useState(1);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });

  // 카카오맵 초기화 - 컴포넌트 마운트 시에는 실행하지 않음
  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 모든 리소스 정리
      if (markerRef.current) {
        try {
          markerRef.current.setMap(null);
        } catch (error) {
          console.log("Marker cleanup error:", error);
        }
        markerRef.current = null;
      }
      if (mapInstanceRef.current) {
        try {
          // 카카오맵 이벤트 리스너 제거
          window.kakao?.maps?.event?.removeListener(mapInstanceRef.current, "click");
        } catch (error) {
          console.log("Map cleanup error:", error);
        }
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // 카카오맵 초기화 (컴포넌트 마운트 시점에는 제거)
  const initializeMap = () => {
    const container = mapRef.current;
    if (!container || !window.kakao || !window.kakao.maps) {
      console.log(
        "지도 초기화 실패 - container:",
        !!container,
        "kakao.maps:",
        !!window.kakao?.maps
      );
      return;
    }

    try {
      const centerLat = selectedLocationDetail?.latitude || 35.0156;
      const centerLng = selectedLocationDetail?.longitude || 126.7108;

      const options = {
        center: new window.kakao.maps.LatLng(centerLat, centerLng),
        level: 5,
      };

      const map = new window.kakao.maps.Map(container, options);
      mapInstanceRef.current = map;
      console.log("지도 초기화 성공");

      // 소재지 위치에 마커 표시
      if (selectedLocationDetail?.latitude && selectedLocationDetail?.longitude) {
        const coords = new window.kakao.maps.LatLng(
          selectedLocationDetail.latitude,
          selectedLocationDetail.longitude
        );

        const marker = new window.kakao.maps.Marker({
          position: coords,
          map: map,
        });
        markerRef.current = marker;
      }

      // 클릭 이벤트 추가
      window.kakao.maps.event.addListener(map, "click", (mouseEvent) => {
        const latlng = mouseEvent.latLng;

        // 기존 마커 제거
        if (markerRef.current) {
          try {
            markerRef.current.setMap(null);
          } catch (error) {
            console.log("Click marker removal error:", error);
          }
          markerRef.current = null;
        }

        // 새 마커 생성
        try {
          const marker = new window.kakao.maps.Marker({
            position: latlng,
            map: map,
          });
          markerRef.current = marker;
        } catch (error) {
          console.log("Click marker creation error:", error);
        }
      });
    } catch (error) {
      console.error("카카오맵 초기화 실패:", error);
    }
  };

  // 선택된 소재지가 변경될 때 상세 정보 로드
  useEffect(() => {
    if (selectedLocation) {
      // 기존 마커 정리
      if (markerRef.current) {
        try {
          markerRef.current.setMap(null);
        } catch (error) {
          console.log("Previous marker cleanup error:", error);
        }
        markerRef.current = null;
      }
      loadLocationDetail(selectedLocation.id);
    }
  }, [selectedLocation]);

  // selectedLocationDetail이 있을 때 지도 초기화
  useEffect(() => {
    if (selectedLocationDetail && mapRef.current) {
      // 카카오맵 로드 확인 및 초기화
      const checkAndInitMap = () => {
        if (window.kakao && window.kakao.maps && window.kakao.maps.LatLng) {
          initializeMap();
        } else if (window.kakao && window.kakao.maps && window.kakao.maps.load) {
          window.kakao.maps.load(() => {
            initializeMap();
          });
        } else {
          // 카카오맵이 아직 로드되지 않았다면 잠시 후 다시 시도
          setTimeout(checkAndInitMap, 100);
        }
      };

      checkAndInitMap();
    }
  }, [selectedLocationDetail]);

  // 소재지 상세 정보 로드
  const loadLocationDetail = async (locationId) => {
    try {
      const response = await getLocationDetailApi(locationId);
      console.log("=== 소재지 상세 정보 ===");
      console.log(response);
      console.log("위도:", response.latitude, "경도:", response.longitude);
      console.log("devices:", response.devices);

      setSelectedLocationDetail(response);

      // 지도가 아직 초기화되지 않았을 수 있으므로 약간의 지연 후 실행
      setTimeout(() => {
        if (response.latitude && response.longitude && mapInstanceRef.current) {
          console.log("지도 업데이트 시도");
          const coords = new window.kakao.maps.LatLng(response.latitude, response.longitude);
          mapInstanceRef.current.setCenter(coords);
          mapInstanceRef.current.setLevel(3); // 줌 레벨 조정

          // 기존 마커 제거
          if (markerRef.current) {
            try {
              markerRef.current.setMap(null);
            } catch (error) {
              console.log("Marker removal error:", error);
            }
            markerRef.current = null;
          }

          // 새 마커 생성
          try {
            const marker = new window.kakao.maps.Marker({
              position: coords,
              map: mapInstanceRef.current,
            });
            markerRef.current = marker;
          } catch (error) {
            console.log("Marker creation error:", error);
          }
          console.log("마커 생성 완료");
        } else {
          console.log(
            "지도 업데이트 실패 - mapInstance:",
            !!mapInstanceRef.current,
            "lat:",
            response.latitude,
            "lng:",
            response.longitude
          );
        }
      }, 500);
    } catch (error) {
      console.error("소재지 상세 정보 로드 실패:", error);
    }
  };

  // 소재지 목록 조회
  const loadLocations = async (page = 0) => {
    try {
      startLoading("소재지 목록을 불러오는 중...");
      const response = await getLocationsApi({
        page,
        size: pagination.size,
        sort: "createdAt,desc",
      });

      setLocations(response.content || []);
      setPagination({
        ...pagination,
        page: response.number || 0,
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0,
      });
    } catch (error) {
      console.error("소재지 목록 조회 실패:", error);
      modal.alert("오류", "소재지 목록을 불러오는데 실패했습니다.");
    } finally {
      stopLoading();
    }
  };

  // 소재지 검색
  const handleSearch = async () => {
    if (!searchKeyword.trim() && !selectedDistrict) {
      loadLocations();
      return;
    }

    try {
      startLoading("검색 중...");

      // 동 선택과 검색어를 조합하여 검색
      let searchQuery = "";
      if (selectedDistrict) {
        searchQuery = selectedDistrict;
        if (searchKeyword.trim()) {
          searchQuery += " " + searchKeyword;
        }
      } else {
        searchQuery = searchKeyword;
      }

      const response = await searchLocationsApi(searchQuery, {
        page: 0,
        size: pagination.size,
      });

      setLocations(response.content || []);
      setPagination({
        ...pagination,
        page: response.number || 0,
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0,
      });
    } catch (error) {
      console.error("검색 실패:", error);
      modal.alert("오류", "검색에 실패했습니다.");
    } finally {
      stopLoading();
    }
  };

  // 소재지 삭제
  const handleDelete = (location) => {
    modal.confirm(
      "삭제 확인",
      `"${location.locationName}" 소재지를 삭제하시겠습니까?`,
      async () => {
        try {
          startLoading("삭제 중...");
          await deleteLocationApi(location.id);
          modal.alert("성공", "소재지가 삭제되었습니다.");
          loadLocations(pagination.page);
        } catch (error) {
          console.error("삭제 실패:", error);
          modal.alert("오류", "삭제에 실패했습니다.");
        } finally {
          stopLoading();
        }
      }
    );
  };

  // 컴포넌트 마운트 시 목록 조회
  useEffect(() => {
    loadLocations();
  }, []);

  // 선택된 센서 정보 가져오기
  const getSelectedSensorInfo = () => {
    console.log("=== getSelectedSensorInfo ===");
    console.log("selectedLocationDetail:", selectedLocationDetail);
    console.log("devices:", selectedLocationDetail?.devices);
    console.log("selectedSensorTab:", selectedSensorTab);

    if (!selectedLocationDetail || !selectedLocationDetail.devices) {
      console.log("devices 없음");
      return null;
    }

    const sensor = selectedLocationDetail.devices.find((d) => d.sensorNumber === selectedSensorTab);
    console.log("찾은 센서:", sensor);
    return sensor;
  };

  // regionCode로 지역명 가져오기
  const getRegionName = (location) => {
    // regionCode가 있으면 regions 데이터에서 찾기
    console.dir(location);
    if (location.regionCode) {
      const region = getRegionByCode(location.regionCode);
      if (region) {
        return region.name;
      }
    }

    // regionCode가 없거나 찾지 못했으면 district 또는 city 반환
    return location.district || location.city;
  };

  // 이미지 URL 가져오기
  const getImageUrl = (path) => {
    if (!path) return "";

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8090";
    const fileName = path.split("/").pop() || path.split("\\").pop();

    return `${API_BASE_URL}/api/files/location-photos/${fileName}`;
  };

  const getDeviceImageUrl = (path) => {
    if (!path) return "";

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8090";
    const fileName = path.split("/").pop() || path.split("\\").pop();

    return `${API_BASE_URL}/api/files/device-photos/${fileName}`;
  };

  // 페이지 변경
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      loadLocations(newPage);
    }
  };

  return (
    <div className="loc_lt01">
      {/* 좌측: 소재지 검색 */}
      <div
        style={{
          width: "33.34%",
          color: "white",
          padding: "20px",
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2 className="loc01_tit01" style={{ margin: 0 }}>
            소재지 검색
          </h2>
          <button
            onClick={() => window.open("/location/register", "_blank")}
            style={{
              padding: "8px 16px",
              backgroundColor: "#e74c3c",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            신규 소재지 등록하기
          </button>
        </div>

        <div className="loc01_bx01" style={{ marginBottom: "20px" }}>
          <select
            value={selectedDistrict}
            onChange={(e) => setSelectedDistrict(e.target.value)}
            style={{
              width: "120px",
              padding: "8px",
              marginRight: "10px",
              border: "1px solid #34495e",
              backgroundColor: "#34495e",
              color: "white",
              borderRadius: "4px",
            }}
          >
            <option value="">동 선택</option>
            {najuDistricts.map((district) => (
              <option key={district.code} value={district.name}>
                {district.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            placeholder="검색어를 입력하세요"
            style={{
              width: "calc(100% - 220px)",
              padding: "8px",
              border: "1px solid #34495e",
              backgroundColor: "#34495e",
              color: "white",
              borderRadius: "4px",
              marginRight: "10px",
            }}
          />
          <button
            onClick={handleSearch}
            style={{
              padding: "8px 16px",
              backgroundColor: "#3498db",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            검색
          </button>
        </div>

        {/* 소재지 검색결과 */}
        <div style={{ marginBottom: "20px" }}>
          <h2 className="loc01_tit01">소재지 검색결과</h2>

          <div className="loc01_dv01">
            <p className="loc01_p01">
              등록소재지 <span>{pagination.totalElements}</span>
            </p>

            <table style={{ width: "100%", fontSize: "14px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #34495e" }}>
                  <th style={{ padding: "10px", textAlign: "left" }}>코드</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>지역</th>
                  <th style={{ padding: "10px", textAlign: "left" }}>소재지명</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr
                    key={location.id}
                    onClick={() => setSelectedLocation(location)}
                    style={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedLocation?.id === location.id ? "#1f5856" : "transparent",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedLocation?.id !== location.id) {
                        e.currentTarget.style.backgroundColor = "#2c3e50";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedLocation?.id !== location.id) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <td style={{ padding: "10px", borderBottom: "1px solid #34495e" }}>
                      {location.locationCode}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #34495e" }}>
                      {getRegionName(location)}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #34495e" }}>
                      {location.locationName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 페이지네이션 */}
            <div style={{ marginBottom: "20px", marginTop: "20px", textAlign: "center" }}>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
                style={{
                  padding: "5px 10px",
                  margin: "0 5px",
                  backgroundColor: pagination.page === 0 ? "#7f8c8d" : "#3498db",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: pagination.page === 0 ? "not-allowed" : "pointer",
                }}
              >
                이전
              </button>
              <span style={{ fontSize: "14px", margin: "0 10px", color: "#ffffff" }}>
                {pagination.page + 1} / {pagination.totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                style={{
                  padding: "5px 10px",
                  margin: "0 5px",
                  backgroundColor:
                    pagination.page >= pagination.totalPages - 1 ? "#7f8c8d" : "#3498db",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: pagination.page >= pagination.totalPages - 1 ? "not-allowed" : "pointer",
                }}
              >
                다음
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 중앙: 소재지 정보 */}
      {selectedLocationDetail && (
        <div
          style={{
            width: "33.33%",
            backgroundColor: "#1a1a1a",
            color: "white",
            padding: "20px",
            overflowY: "auto",
          }}
        >
          <h2 className="loc01_tit01" style={{ marginTop: "37px" }}>
            소재지 정보
          </h2>

          <div className="loc01_dv02" style={{ backgroundColor: "#1c1c1c" }}>
            <table style={{ width: "100%", fontSize: "14px" }}>
              <tbody>
                <tr>
                  <td
                    style={{
                      padding: "10px",
                      width: "138px",
                      color: "#a5a3a3",
                      backgroundColor: "#3b3b3b",
                    }}
                  >
                    공개여부
                  </td>
                  <td style={{ padding: "10px" }}>전체공개</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    소재지명
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.locationName}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    등록번호
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.locationCode}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    도로명
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.fullAddress || "-"}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    지번
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.fullAddress || "-"}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    상세주소
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.detailAddress || "-"}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    대표
                  </td>
                  <td style={{ padding: "10px" }}>
                    {selectedLocationDetail.representative || "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    담당자
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.manager || "-"}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    연락처1
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.contact || "-"}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    연락처2
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.email || "-"}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    조업일
                  </td>
                  <td style={{ padding: "10px" }}>월/화/수/목/금</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    조업시간
                  </td>
                  <td style={{ padding: "10px" }}>09:00~18:00</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    업종
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.locationType}</td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    사업자번호
                  </td>
                  <td style={{ padding: "10px" }}>
                    {selectedLocationDetail.businessNumber || "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    설립일
                  </td>
                  <td style={{ padding: "10px" }}>
                    {selectedLocationDetail.establishmentDate || "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "10px", color: "#a5a3a3", backgroundColor: "#3b3b3b" }}>
                    기타특이사항
                  </td>
                  <td style={{ padding: "10px" }}>{selectedLocationDetail.notes || "-"}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ marginTop: "20px", textAlign: "right" }}></div>
          </div>
        </div>
      )}

      {/* 우측: 지도 및 센서 정보 */}
      {selectedLocationDetail && (
        <div style={{ width: "33.33%", padding: "20px", backgroundColor: "#282828" }}>
          <div style={{ marginBottom: "5px", textAlign: "right" }}>
            <button
              onClick={() => window.open(`/location/edit/${selectedLocationDetail.id}`, "_blank")}
              style={{
                padding: "8px 16px",
                backgroundColor: "#ffffff",
                color: "#333333",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginRight: "10px",
              }}
            >
              수정
            </button>
            <button
              onClick={() => handleDelete(selectedLocationDetail)}
              style={{
                padding: "8px 16px",
                backgroundColor: "#ffffff",
                color: "#333333",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              삭제
            </button>
          </div>
          <h2 className="loc01_tit01">소재지 위치정보</h2>
          <div
            className="loc_pic_dv01"
            style={{
              marginBottom: "20px",
            }}
          >
            <div className="loc_pic_dv_lt">
              <div style={{ border: "1px solid #424242" }}>
                <div>
                  <p
                    style={{
                      padding: "9px 20px",
                      fontSize: "14px",
                      color: "#ffffff",
                      lineHeight: "24px",
                    }}
                  >
                    위도: {selectedLocationDetail.latitude || "없음"}, 경도:{" "}
                    {selectedLocationDetail.longitude || "없음"}
                  </p>
                </div>

                {/* 지도 영역 */}
                <div
                  ref={mapRef}
                  style={{
                    width: "100%",
                    height: "225px",
                    border: "1px solid #ddd",
                  }}
                />
              </div>
            </div>
            <div className="loc_pic_dv_rt">
              <div>
                <div className="loc_pic_dv02">
                  {selectedLocationDetail.photos && selectedLocationDetail.photos.length > 0
                    ? selectedLocationDetail.photos.slice(0, 3).map((photo, index) => (
                        <div
                          key={index}
                          style={{
                            height: "85px",
                            backgroundColor: "#3b3b3b",
                            border: "1px solid #424242",
                            position: "relative",
                            overflow: "hidden",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            // 이미지 클릭 시 새 창으로 크게 보기
                            window.open(getImageUrl(photo.fileUrl || photo.filePath), "_blank");
                          }}
                        >
                          <img
                            src={getImageUrl(photo.fileUrl || photo.filePath)}
                            alt={`사업장 사진 ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              backgroundColor: "#f0f0f0",
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                              // DOM 조작 대신 상태나 클래스 변경으로 처리
                              target.setAttribute('data-error', 'true');
                            }}
                          />
                        </div>
                      ))
                    : // 사진이 없을 때 빈 박스 3개 표시
                      [0, 1, 2].map((index) => (
                        <div
                          key={index}
                          style={{
                            flex: 1,
                            height: "85px",
                            backgroundColor: "#3b3b3b",
                            border: "1px solid #424242",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#7f8c8d",
                            fontSize: "12px",
                          }}
                        >
                          이미지
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </div>

          <h2 className="loc01_tit01">소재지 내 설치 센서 정보</h2>

          <div style={{ marginBottom: "10px" }}>
            <span style={{ color: "#7f8c8d" }}>설치센서 </span>
            <span style={{ color: "#e74c3c", fontWeight: "bold" }}>
              {selectedLocationDetail.devices?.length ||
                selectedLocationDetail.activeDeviceCount ||
                0}
              개
            </span>
          </div>

          {/* 센서 탭 */}
          <div style={{ display: "flex", marginBottom: "20px" }}>
            {[1, 2, 3, 4, 5].map((num) => (
              <button
                key={num}
                onClick={() => setSelectedSensorTab(num)}
                style={{
                  flex: 1,
                  padding: "10px",
                  backgroundColor: num === selectedSensorTab ? "#3498db" : "#ecf0f1",
                  color: num === selectedSensorTab ? "white" : "#7f8c8d",
                  border: "1px solid #bdc3c7",
                  cursor: "pointer",
                }}
              >
                sensor{num}
              </button>
            ))}
          </div>

          <div
            className="loc_pic_dv01"
            style={{
              marginBottom: "20px",
            }}
          >
            <div className="loc_pic_dv_lt">
              {/* 센서 상세 정보 */}
              <div>
                {(() => {
                  const sensorInfo = getSelectedSensorInfo();
                  const device = sensorInfo?.device;

                  return (
                    <div className="loc01_dv02">
                      <table style={{ width: "100%", fontSize: "14px" }}>
                        <tbody>
                          <tr>
                            <td
                              style={{
                                padding: "10px",
                                width: "138px",
                                color: "#a5a3a3",
                                backgroundColor: "#3b3b3b",
                              }}
                            >
                              아이디
                            </td>
                            <td style={{ padding: "10px" }}>
                              {device?.deviceId || sensorInfo?.deviceCode || "-"}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                padding: "10px",
                                width: "138px",
                                color: "#a5a3a3",
                                backgroundColor: "#3b3b3b",
                              }}
                            >
                              구역
                            </td>
                            <td style={{ padding: "10px" }}>
                              {device?.regionFullName || (device ? selectedLocationDetail.district : "-") || "-"}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                padding: "10px",
                                width: "138px",
                                color: "#a5a3a3",
                                backgroundColor: "#3b3b3b",
                              }}
                            >
                              기기명
                            </td>
                            <td style={{ padding: "10px" }}>
                              {sensorInfo?.sensorAlias ||
                                device?.installation?.deviceName ||
                                device?.brand ||
                                "-"}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                padding: "10px",
                                width: "138px",
                                color: "#a5a3a3",
                                backgroundColor: "#3b3b3b",
                              }}
                            >
                              위도/경도
                            </td>
                            <td style={{ padding: "10px" }}>
                              {device?.installation?.latitude && device?.installation?.longitude
                                ? `${device.installation.latitude} / ${device.installation.longitude}`
                                : "-"}
                            </td>
                          </tr>
                          <tr>
                            <td
                              style={{
                                padding: "10px",
                                width: "138px",
                                color: "#a5a3a3",
                                backgroundColor: "#3b3b3b",
                              }}
                            >
                              상세위치정보
                            </td>
                            <td style={{ padding: "10px" }}>
                              {sensorInfo?.installationPosition ||
                                device?.installation?.locationDetail ||
                                "-"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="loc_pic_dv_rt">
              {/* 센서 이미지 */}
              <div>
                <div className="loc_pic_dv02">
                  {(() => {
                    const sensorInfo = getSelectedSensorInfo();
                    const devicePhotos = sensorInfo?.device?.photos || [];

                    return [0, 1, 2].map((index) => {
                      const photo = devicePhotos[index];

                      return (
                        <div
                          key={index}
                          style={{
                            height: "85px",
                            backgroundColor: "#3b3b3b",
                            border: "1px solid #424242",
                            position: "relative",
                            overflow: "hidden",
                            cursor: photo ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (photo) {
                              // 이미지 클릭 시 새 창으로 크게 보기
                              window.open(getDeviceImageUrl(photo.fileUrl || photo.filePath || photo.url), "_blank");
                            }
                          }}
                        >
                          {photo ? (
                            <img
                              src={getDeviceImageUrl(photo.fileUrl || photo.filePath || photo.url)}
                              alt={`기기 사진 ${index + 1}`}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                backgroundColor: "#f0f0f0",
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = "none";
                                // DOM 조작 대신 상태나 클래스 변경으로 처리
                                target.setAttribute('data-error', 'true');
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                color: "#7f8c8d",
                                fontSize: "12px",
                              }}
                            >
                              이미지
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationListView;
